import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  ListItemText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useTheme } from "@mui/material/styles";
import DateRangeCalendar from "screens/Customers/component/DateRangeCalendar";
import CustomLabel from "components/CustomLabel";
import dayjs from "dayjs";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CustomAutocomplete from "components/mui/autocomplete";

const LoginActivityReport = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [sessionDuration, setSessionDuration] = useState("");
  const open = Boolean(anchorEl);

  // Available device/browser options
  const deviceOptions = [
    { value: "chrome", label: "Chrome" },
    { value: "safari", label: "Safari" },
    { value: "edge", label: "Edge" },
    { value: "firefox", label: "Firefox" },
    { value: "mobile", label: "Mobile" },
  ];
  const [selectedDevice, setSelectedDevice] = useState([]);

  // Handle export menu
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = (option) => {
    setAnchorEl(null);
    if (option) {
      console.log("Export as:", option);
    }
  };

  // Handle multi-select for device/browser
  const handleDeviceChange = (event) => {
    const value = event.target.value;
    if (value.includes("all")) {
      // Toggle select all
      if (selectedDevices.length === deviceOptions.length) {
        setSelectedDevices([]);
      } else {
        setSelectedDevices(deviceOptions.map((opt) => opt.value));
      }
    } else {
      setSelectedDevices(value);
    }
  };

  // Check if all devices selected
  const allSelected = selectedDevices.length === deviceOptions.length;

  // Example rows (with fixed mm/dd/yyyy hh:mm:ss format)
  const rows = [
    {
      id: 1,
      userId: "UI001",
      userName: "Brooklyn Simmons",
      loginTime: dayjs("2025-08-25 08:15:00").format("MM/DD/YYYY HH:mm:ss"),
      logoutTime: dayjs("2025-08-25 10:45:00").format("MM/DD/YYYY HH:mm:ss"),
      duration: "02:30:00",
      ipAddress: "192.168.1.45",
      device: "Chrome (Windows)",
    },
    {
      id: 2,
      userId: "UI002",
      userName: "Cody Fisher",
      loginTime: dayjs("2025-08-25 11:15:00").format("MM/DD/YYYY HH:mm:ss"),
      logoutTime: dayjs("2025-08-25 11:35:00").format("MM/DD/YYYY HH:mm:ss"),
      duration: "00:20:00",
      ipAddress: "10.0.0.22",
      device: "Safari (iOS)",
    },
    {
      id: 3,
      userId: "UI003",
      userName: "Robert Fox",
      loginTime: dayjs("2025-08-25 08:15:00").format("MM/DD/YYYY HH:mm:ss"),
      logoutTime: dayjs("2025-08-26 04:15:00").format("MM/DD/YYYY HH:mm:ss"),
      duration: "20:00:00",
      ipAddress: "172.16.5.18",
      device: "Edge (Windows)",
    },
  ];

  const columns = [
    { field: "userId", headerName: "User ID", flex: 1, minWidth: 100 },
    { field: "userName", headerName: "User Name", flex: 1, minWidth: 150 },
    { field: "loginTime", headerName: "Login Time", flex: 1, minWidth: 160 },
    { field: "logoutTime", headerName: "Logout Time", flex: 1, minWidth: 160 },
    { field: "duration", headerName: "Duration", flex: 1, minWidth: 120 },
    { field: "ipAddress", headerName: "IP Address", flex: 1, minWidth: 130 },
    { field: "device", headerName: "Device", flex: 1, minWidth: 160 },
  ];

  const handleNavigation = (to) => navigate(to);
  const handlePageBack = () => handleNavigation("/reports");

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Login Activity Report"
    >
      <Card
        sx={{
          p: 3,
          boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          height: "100%",
          width: "100%",
          overflowX: "auto",
          mx: "auto",
          mt: 4,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              p: 3,
              bgcolor: "white",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h4">Login Activity Report</Typography>
                <Typography variant="body1" color="text.secondary">
                  Monitor system usage patterns and user activity
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="main"
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
              >
                Export
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose(null)}
                sx={{ "& .MuiPaper-root": { minWidth: "110px" } }}
              >
                <MenuItem onClick={() => handleClose("csv")}>CSV</MenuItem>
                <MenuItem onClick={() => handleClose("xls")}>XLS</MenuItem>
                <MenuItem onClick={() => handleClose("pdf")}>PDF</MenuItem>
              </Menu>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Filters */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "#F8FAFC" }}
              >
                <Typography variant="h5">Filters</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: "#F8FAFC" }}>
                <Grid container spacing={3}>
                  {/* First Row */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      User Name / User ID
                    </Typography>

                    <CustomAutocomplete
                      fullWidth
                      size="small"
                      options={["UI001", "UI002", "UI003", "UI004", "UI005"]}
                      getOptionLabel={(opt) => opt}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          placeholder="Search user..."
                          size="small"
                          sx={{
                            width: "300px !important",
                            "& .MuiInputBase-root": {
                              border: "1px solid #D9D9D9",
                              boxShadow: "none",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#1B2431"
                                  : "#fff !important",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Date Range */}
                  <Grid item xs={12} sm={6} md={3}>
                    <CustomLabel label="Date Range" />
                    <DateRangeCalendar
                      initialStartDate={dayjs()}
                      initialEndDate={dayjs()}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        order: { xs: 4, sm: 3 },
                      }}
                    />
                  </Grid>

                  {/* Device Type Multi-select */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ mb: 0, fontWeight: 500 }}>
                      Device / Browser Type
                    </Typography>
                    <CustomTextField
                      select
                      size="small"
                      fullWidth
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected.length) {
                            return "Select Type";
                          }
                          return selected.join(", ");
                        },
                      }}
                      value={selectedDevice}
                      onChange={(event) => {
                        const value = event.target.value;
                        if (value.includes("all")) {
                          if (selectedDevice.length === deviceOptions.length) {
                            // Deselect all
                            setSelectedDevice([]);
                          } else {
                            // Select all
                            setSelectedDevice(
                              deviceOptions.map((opt) => opt.value)
                            );
                          }
                        } else {
                          setSelectedDevice(value);
                        }
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          width: "100% !important",
                          height: "40px",
                          border: "1px solid #D9D9D9",
                          boxShadow: "none",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          width: "100% !important",
                        },
                        "& .MuiInputBase-input": {
                          width: "100% !important",
                        },
                      }}
                    >
                      <MenuItem value="all">
                        <em>Select All</em>
                      </MenuItem>
                      {deviceOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Grid>

                  {/* Duration */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Session Duration Range
                    </Typography>
                    <CustomTextField
                      select
                      size="small"
                      fullWidth
                      value={sessionDuration}
                      onChange={(e) => setSessionDuration(e.target.value)}
                      SelectProps={{
                        displayEmpty: true,
                        IconComponent: AccessTimeIcon,
                        renderValue: (selected) => {
                          if (!selected) return "Select Duration";
                          return selected;
                        },
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          border: "1px solid #D9D9D9",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      <MenuItem value="less-than-1h">Less than 1 hour</MenuItem>
                      <MenuItem value="1h-4h">1-4 hours</MenuItem>
                      <MenuItem value="4h-8h">4-8 hours</MenuItem>
                      <MenuItem value="more-than-8h">
                        More than 8 hours
                      </MenuItem>
                    </CustomTextField>
                  </Grid>
                </Grid>

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                >
                  <Button variant="outlined" color="main" size="small">
                    Run Filter
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* DataGrid */}
            <Box sx={{ mt: 6 }}>
              <DataGridPro
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                // pageSizeOptions={[5, 10, 50]}
                // initialState={{
                //   pagination: { paginationModel: { pageSize: 5 } },
                // }}
                slots={{
                  footer: () => null
                }}
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    textTransform: "none",
                    fontWeight: 600,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#F8FAFC",
                  },
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default LoginActivityReport;
